export type GenerativeBlock =
	| "theme"
	| "nav"
	| "hero"
	| "sections"
	| "footer";

export type WebsiteRebuild = {
	state: "running" | "done" | "failed";
	startedAt: string | null;
	finishedAt: string | null;
	message: string | null;
	log: string[];
};

export type WebsiteLive = {
	origin: "redesign" | "clone";
	status: string;
	phase: string;
	progress: number;
	name: string;
	lastFile: string | null;
	filesWritten: string[];
	keep: Array<{ label: string; reason: string }>;
	add: Array<{ label: string; reason: string }>;
	drop: Array<{ label: string; reason: string }>;
	lastError: string | null;
	previewUrl: string | null;
	publishedUrl: string | null;
	rebuild: WebsiteRebuild | null;
	studio: WebsiteStudio | null;
};

export type WebsiteStudio = {
	live: boolean;
	log: string[];
	steps: string[];
};

export function parseWebsiteLive(site: Record<string, unknown>): WebsiteLive {
	return {
		origin: site.origin === "redesign" ? "redesign" : "clone",
		status: typeof site.status === "string" ? site.status : "cloning",
		phase: typeof site.phase === "string" ? site.phase : "progress",
		progress: typeof site.progress === "number" ? site.progress : 0,
		name: typeof site.name === "string" ? site.name : "",
		lastFile: typeof site.lastFile === "string" ? site.lastFile : null,
		filesWritten: asFiles(site.filesWritten),
		keep: asDecisions(site.keep),
		add: asDecisions(site.add),
		drop: asDecisions(site.drop),
		lastError: typeof site.lastError === "string" ? site.lastError : null,
		previewUrl: typeof site.previewUrl === "string" ? site.previewUrl : null,
		publishedUrl:
			typeof site.publishedUrl === "string" ? site.publishedUrl : null,
		rebuild: asRebuild(site.meta),
		studio: asStudio(site.meta),
	};
}

function asStudio(meta: unknown): WebsiteStudio | null {
	if (!meta || typeof meta !== "object") return null;
	const raw = (meta as { dev?: unknown }).dev;
	if (!raw || typeof raw !== "object") return null;
	const rec = raw as { live?: unknown; log?: unknown; steps?: unknown };
	return {
		live: rec.live === true,
		log: asFiles(rec.log),
		steps: asFiles(rec.steps),
	};
}

function asRebuild(meta: unknown): WebsiteRebuild | null {
	if (!meta || typeof meta !== "object") return null;
	const raw = (meta as { rebuild?: unknown }).rebuild;
	if (!raw || typeof raw !== "object") return null;
	const rec = raw as {
		state?: unknown;
		startedAt?: unknown;
		finishedAt?: unknown;
		message?: unknown;
		log?: unknown;
	};
	if (rec.state !== "running" && rec.state !== "done" && rec.state !== "failed") {
		return null;
	}
	return {
		state: rec.state,
		startedAt: typeof rec.startedAt === "string" ? rec.startedAt : null,
		finishedAt: typeof rec.finishedAt === "string" ? rec.finishedAt : null,
		message: typeof rec.message === "string" ? rec.message : null,
		log: asFiles(rec.log),
	};
}

export function websitePreviewReady(live: WebsiteLive | null): boolean {
	if (!live) return false;
	if (live.studio?.live) return true;
	return live.status === "preview_ready" || live.phase === "ready";
}

function asDecisions(raw: unknown): Array<{ label: string; reason: string }> {
	if (!Array.isArray(raw)) return [];
	return raw.flatMap((row) => {
		if (!row || typeof row !== "object") return [];
		const rec = row as { label?: unknown; reason?: unknown };
		if (typeof rec.label !== "string") return [];
		return [
			{
				label: rec.label,
				reason: typeof rec.reason === "string" ? rec.reason : "",
			},
		];
	});
}

function asFiles(raw: unknown): string[] {
	if (!Array.isArray(raw)) return [];
	return raw.filter((row): row is string => typeof row === "string");
}

const BLOCK_ORDER: readonly GenerativeBlock[] = [
	"theme",
	"nav",
	"hero",
	"sections",
	"footer",
];

/**
 * Map Astro paths onto page regions so Website can light up as files land.
 * Tokens first, chrome next, then the page body.
 */
export function blocksFromFiles(
	files: readonly string[],
	lastFile?: string | null,
): Set<GenerativeBlock> {
	const lit = new Set<GenerativeBlock>();
	for (const file of files) {
		const block = blockForFile(file);
		if (block) lit.add(block);
	}
	if (lastFile) {
		const current = blockForFile(lastFile);
		if (current) lit.add(current);
	}
	return lit;
}

export function blockForFile(path: string): GenerativeBlock | null {
	const lower = path.toLowerCase();
	if (
		/(^|\/)(brand\.json|tokens\.css|global\.css|styles\.css)$/.test(lower) ||
		lower.includes("/styles/")
	) {
		return "theme";
	}
	if (/(header|nav|layout)\./.test(lower)) {
		return "nav";
	}
	if (/(footer)\./.test(lower)) {
		return "footer";
	}
	if (/(hero|cta)\./.test(lower) || /pages\/index\.(astro|tsx|html)$/.test(lower)) {
		return "hero";
	}
	if (/\/(components|pages)\//.test(lower)) {
		return "sections";
	}
	return null;
}

export function nextPendingBlock(
	lit: ReadonlySet<GenerativeBlock>,
): GenerativeBlock | null {
	for (const block of BLOCK_ORDER) {
		if (!lit.has(block)) return block;
	}
	return null;
}
